<template>
                <div v-if="currentView === 'server_ssl'">
                    <div class="page-header"><div><div class="page-title">SSL 憑證管理</div><div class="page-subtitle">查看憑證狀態與執行熱重載</div></div></div>

                    <div class="card p-6 mb-6">
                        <h3 class="font-bold mb-4" style="color:var(--gray-700);">目前憑證狀態</h3>
                        <div v-if="sslInfo && Array.isArray(sslInfo.certs) && sslInfo.certs.length" class="space-y-3">
                            <div class="text-sm" style="color:var(--gray-600);">
                                找到 {{ sslInfo.certs.length }} 組憑證<span v-if="sslInfo.live_dir">（{{ sslInfo.live_dir }}）</span>
                            </div>
                            <div class="overflow-x-auto border rounded">
                                <table class="data-table">
                                    <thead>
                                        <tr>
                                            <th class="p-2 w-64">網域</th>
                                            <th class="p-2 w-32">狀態</th>
                                            <th class="p-2 w-64">到期日</th>
                                            <th class="p-2 w-28">剩餘</th>
                                            <th class="p-2 w-56">更新時間</th>
                                            <th class="p-2 w-24">操作</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="c in sslInfo.certs" :key="c.domain" class="border-b">
                                            <td class="p-2 font-mono break-all">{{ c.domain }}</td>
                                            <td class="p-2">
                                                <span class="badge"
                                                    :class="(c.fullchain_exists && c.privkey_exists) ? 'badge-success' : 'badge-danger'">
                                                    {{ (c.fullchain_exists && c.privkey_exists) ? '完整' : '缺檔' }}
                                                </span>
                                            </td>
                                            <td class="p-2">
                                                <span v-if="c.valid_to">{{ new Date(c.valid_to).toLocaleString() }}</span>
                                                <span v-else style="color:var(--gray-400);">—</span>
                                            </td>
                                            <td class="p-2">
                                                <span v-if="c.days_remaining !== undefined && c.days_remaining !== null"
                                                    :style="Number(c.days_remaining) < 30 ? 'color:var(--color-danger);font-weight:700' : 'color:var(--color-success);font-weight:700'">
                                                    {{ c.days_remaining }} 天
                                                </span>
                                                <span v-else style="color:var(--gray-400);">—</span>
                                            </td>
                                            <td class="p-2">
                                                <span v-if="c.fullchain_mtime">{{ new Date(Number(c.fullchain_mtime) * 1000).toLocaleString() }}</span>
                                                <span v-else style="color:var(--gray-400);">—</span>
                                            </td>
                                            <td class="p-2">
                                                <button @click="deleteLeCert(c.domain)" :disabled="!isSuperAdmin"
                                                    class="btn btn-xs btn-danger">移除</button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-else class="py-4" style="color:var(--gray-500);">尚未找到 Let's Encrypt live 憑證。</div>
                    </div>

                    <!-- Let's Encrypt Section -->
                    <div class="card p-6 mb-6">
                        <h3 class="font-bold mb-4" style="color:var(--gray-700);">Let's Encrypt 自動憑證 (Certbot)</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- List -->
                            <div>
                                <h4 class="font-bold text-sm mb-2">已申請憑證</h4>
                                <div v-if="letsEncryptCerts.length === 0" class="text-sm" style="color:var(--gray-500);">尚無 Let's Encrypt
                                    憑證。</div>
                                <ul v-else class="space-y-2">
                                    <li v-for="cert in letsEncryptCerts" :key="cert.domain"
                                        class="input flex justify-between items-center gap-2" style="background:var(--gray-50);">
                                        <div class="flex-1 min-w-0">
                                            <div class="font-bold text-sm">{{ cert.domain }}</div>
                                            <div class="text-xs" style="color:var(--gray-500);">
                                                <span v-if="cert.valid_to">剩餘: {{ cert.days_remaining }} 天 ({{ cert.valid_to.split('T')[0] }})</span>
                                                <span v-else>更新: {{ cert.mtime ? new Date(Number(cert.mtime) * 1000).toLocaleString() : '—' }}</span>
                                            </div>
                                        </div>
                                        <div class="flex gap-1 shrink-0">
                                            <button @click="applyLeCert(cert.domain)" :disabled="!isSuperAdmin"
                                                class="btn btn-xs btn-success text-white">套用</button>
                                            <button @click="deleteLeCert(cert.domain)" :disabled="!isSuperAdmin"
                                                class="btn btn-xs btn-danger">移除</button>
                                        </div>
                                    </li>
                                </ul>
                                <div v-if="!isSuperAdmin" class="text-xs mt-2" style="color:var(--gray-500);">僅超級管理員可套用 Let's Encrypt 憑證。</div>
                            </div>
                            <!-- Request Form -->
                            <div>
                                <h4 class="font-bold text-sm mb-2">申請新憑證</h4>
                                <div class="space-y-2">
                                    <div>
                                        <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">網域名稱</label>
                                        <input v-model="newLeCert.domain" type="text" :disabled="!isSuperAdmin"
                                            placeholder="mail.example.com"
                                            class="input w-full">
                                    </div>
                                    <div>
                                        <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">聯絡 Email</label>
                                        <input v-model="newLeCert.email" type="email" :disabled="!isSuperAdmin"
                                            placeholder="admin@example.com"
                                            class="input w-full">
                                    </div>
                                    <button @click="requestLeCert" :disabled="!isSuperAdmin"
                                        class="btn btn-primary w-full text-sm">
                                        向 Let's Encrypt 申請憑證
                                    </button>
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">
                                        注意：網域 A 記錄必須指向此伺服器 IP，且防火牆/路由器需開放 TCP port 80（Let's Encrypt HTTP-01 驗證用）。
                                        若 port 80 無法開放，請改用 DNS-01 方式（需手動在 DNS 加 TXT 記錄）。
                                    </p>
                                    <div v-if="!isSuperAdmin" class="text-xs" style="color:var(--gray-500);">僅超級管理員可申請 Let's Encrypt 憑證。</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Generate Self-Signed -->
                        <div class="card p-6">
                            <h3 class="font-bold mb-4" style="color:var(--gray-700);">生成自簽憑證</h3>
                            <p class="text-sm mb-4" style="color:var(--gray-500);">僅用於測試環境。瀏覽器會顯示不安全警告。</p>
                            <div class="space-y-4">
                                <div>
                                    <label class="block text-sm font-bold mb-1">網域名稱</label>
                                    <input v-model="sslForm.domain" type="text" class="input w-full"
                                        placeholder="mail.example.com">
                                </div>
                                <button @click="generateSelfSignedCert"
                                    class="btn btn-warning w-full">
                                    生成自簽憑證
                                </button>
                            </div>
                        </div>

                        <!-- Upload Certificate -->
                        <div class="card p-6">
                            <h3 class="font-bold mb-4" style="color:var(--gray-700);">上傳憑證</h3>
                            <p class="text-sm mb-4" style="color:var(--gray-500);">上傳由 CA 發行的正式憑證 (.crt) 和私鑰 (.key)。</p>
                            <div class="space-y-4">
                                <div>
                                    <label class="block text-sm font-bold mb-1">憑證檔案 (.crt)</label>
                                    <input type="file" accept=".crt,.pem" @change="handleSslUpload($event, 'cert')"
                                        class="input w-full">
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-1">私鑰檔案 (.key)</label>
                                    <input type="file" accept=".key,.pem" @change="handleSslUpload($event, 'key')"
                                        class="input w-full">
                                </div>
                                <button @click="uploadSslCert"
                                    class="btn btn-primary w-full">
                                    上傳並套用
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Security Wizard -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  sslInfo, letsEncryptCerts, newLeCert, sslForm, isSuperAdmin,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  applyLeCert, requestLeCert, deleteLeCert, generateSelfSignedCert, handleSslUpload, uploadSslCert,
} = store
</script>

